// useActorNames.ts — CTSK-402. Batch-resolves actor ULIDs to display
// names for surfaces that only get a raw `actorId` back (the legacy
// `/documents/:id/activity` endpoint, backed by `auditLog`, unlike the
// newer `/audit` endpoint AuditHistoryDrawer/AuditPanel use, which
// already batch-resolves `actor` server-side). Client-side companion
// to that same "resolved name, visible fallback" contract:
//   - null actorId          -> caller renders 'system'
//   - resolvable ULID       -> display name
//   - unresolvable ULID     -> caller renders 'unknown user' (never
//                              the raw ULID — see AuditHistoryDrawer's
//                              actorLabel() for the sibling contract
//                              on the resolved-server-side surfaces)
//
// Gated on the `users:view` capability so callers without it don't
// spray 403s — same "degrade to the fallback, don't crash" approach
// AuditHistoryDrawer takes for `audit:view`.

import { useQueries } from '@tanstack/react-query';
import { apiSingle } from '../lib/api.js';
import { useSession } from './useSession.js';

interface UserRow {
  id: string;
  displayName: string;
}

/** Returns a map of actorId -> resolved display name, or `null` when
 *  the id is known-unresolvable (404, lacking cap, or query error).
 *  Ids still in flight are simply absent from the map — callers should
 *  treat a missing key the same as `null` (brief fallback) until the
 *  query settles. */
export function useActorNames(actorIds: Array<string | null | undefined>): Record<string, string | null> {
  const { session } = useSession();
  const canView = session?.capabilities?.includes('users:view') ?? false;

  const uniqueIds = Array.from(
    new Set(actorIds.filter((id): id is string => typeof id === 'string' && id.length > 0)),
  );

  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['actor-name', id],
      queryFn: () => apiSingle<UserRow>(`/users/${id}`),
      enabled: canView,
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  const map: Record<string, string | null> = {};
  uniqueIds.forEach((id, i) => {
    const r = results[i];
    if (!r) return;
    if (!canView || r.isError) {
      map[id] = null;
    } else if (r.data) {
      map[id] = r.data.displayName;
    }
    // else: still loading — leave unset, caller falls back to 'unknown user'.
  });
  return map;
}
